import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Switch,
  TextField as InputField,
} from "@material-ui/core";

import { Button, useNotify, useRefresh } from "react-admin";

import { CancelIcon, EmailIcon } from "./iconography";
import { REGEX_MULTI_EMAIL } from "../../utils/constants";

export const SendPOSDialogButton = ({
  record,
  label,
  title,
  email,
  subjectDefault,
  enclosePhotosDefault,
  encloseDocsDefault,
  handleSend,
  operatingAccount,
  ...props
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [toEmail, setToEmail] = useState(email);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [ccEmail, setCcEmail] = useState("");
  const [subject, setSubject] = useState(subjectDefault || "");
  const [enclosePhotos, setEnclosePhotos] = useState(enclosePhotosDefault);
  const [encloseDocs, setEncloseDocs] = useState(encloseDocsDefault);
  const [emailOpen, setEmailOpen] = useState(false);

  // The subject is required: an empty field reads as red the moment it is
  // cleared, rather than silently falling back to the account's template.
  const subjectMissing = !subject.trim();

  const handleEmailChange = (event) => {
    const emailInput = event.target.value;
    setToEmail(emailInput);
  };

  const handleEmailBlur = () => {
    setIsEmailValid(validateEmail(toEmail));
  };

  const validateEmail = (email) => {
    if (email) return REGEX_MULTI_EMAIL.test(email.trim());
    return true;
  };

  useEffect(() => {
    if (email) {
      setToEmail(email);
      setEnclosePhotos(enclosePhotosDefault);
      setEncloseDocs(encloseDocsDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    setSubject(subjectDefault || "");
  }, [subjectDefault]);

  const handleDialogOpen = () => setEmailOpen(true);
  const handleDialogClose = () => setEmailOpen(false);

  const handleSubmit = async () => {
    const valid = validateEmail(toEmail);
    if (valid) setIsEmailValid(valid);
    else return;

    if (subjectMissing) return;

    setTimeout(() => {
      handleSend(
        record,
        toEmail,
        ccEmail,
        enclosePhotos || false,
        encloseDocs || false,
        notify,
        refresh,
        operatingAccount,
        subject,
      );
    }, 0);

    setEmailOpen(false);
  };

  return (
    <>
      <Button
        {...props}
        label={label}
        color="primary"
        onClick={handleDialogOpen}
      >
        <EmailIcon />
      </Button>
      {emailOpen && (
        <Dialog
          open={emailOpen}
          maxWidth="sm"
          onClose={handleDialogClose}
          fullWidth
        >
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <InputField
                  label="To email"
                  size="small"
                  variant="outlined"
                  fullWidth
                  required
                  value={toEmail}
                  error={!isEmailValid} // Display error state based on validation
                  helperText={
                    !isEmailValid
                      ? "Please enter a valid email address."
                      : false
                  }
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  label="CC"
                  size="small"
                  variant="outlined"
                  fullWidth
                  helperText="Comma-separated email(s)"
                  value={ccEmail}
                  onChange={(event) => setCcEmail(event.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  label="Subject"
                  size="small"
                  variant="outlined"
                  fullWidth
                  required
                  value={subject}
                  error={subjectMissing}
                  helperText={subjectMissing ? "Required" : false}
                  onChange={(event) => setSubject(event.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <Switch
                  name="attach-photos"
                  color="primary"
                  checked={enclosePhotos}
                  onChange={() => setEnclosePhotos(!enclosePhotos)}
                />
                <Typography component="span">Enclose Photos</Typography>
              </Grid>
              <Grid item xs={6}>
                <Switch
                  name="attach-docs"
                  color="primary"
                  checked={encloseDocs}
                  onChange={() => setEncloseDocs(!encloseDocs)}
                />
                <Typography component="span">Enclose Attachments</Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button label="CANCEL" color="default" onClick={handleDialogClose}>
              <CancelIcon />
            </Button>
            <Button label="SEND" color="primary" onClick={handleSubmit}>
              <EmailIcon />
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
